import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mic, Send, Clock, CheckCircle, Lock, PlaneTakeoff, FileText, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api, { gatesAPI, gateQueueAPI, templatesAPI } from '../../services/api';
import { useMyActiveGateSession } from '../../hooks/useMyActiveGateSession';
import { useGateFlightContext } from '../../hooks/useGateFlightContext';
import { knownPlaceholderValues, fillEmpty, isAirportTemplate } from '../../utils/templatePrefill';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';

// CR 8.3 — every requested announcement needs one of three playback-timing methods.
const PREVIEW_DEBOUNCE_MS = 400;

const TIMING_OPTIONS = [
  { value: 'relative', label: 'In N minutes' },
  { value: 'clock', label: 'At a specific time' },
  { value: 'sequence', label: 'After another announcement' },
];

export default function AdHocPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  // Manager/Supervisor may request for any gate; an Operator may only request for
  // the gate they're currently working (CR 4.1 — one gate per operator at a time).
  // Sourced from the operator's own active session, never from the URL param, so
  // it can't be spoofed by editing ?gate_id= — the backend enforces this too.
  const isOperator = user?.role === 'gate_operator';
  const { session: mySession } = useMyActiveGateSession();
  // CR 4.3: the gate entered at session-start auto-attaches to requests made during
  // that session — arriving with ?gate_id= (from the "Request Ad-Hoc" link inside a
  // session) pre-fills the picker for non-operator roles too, but stays editable.
  const paramGateId = searchParams.get('gate_id') || '';
  const lockedGateId = isOperator ? (mySession ? String(mySession.gate_id) : '') : paramGateId;
  const [text, setText] = useState('');
  const [gates, setGates] = useState([]);
  const [gateId, setGateId] = useState(lockedGateId);
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(null);

  // Same admin-managed Template library as the "Templates" admin page — not a
  // hardcoded local list. Read-only access is open to any authenticated role
  // (only create/update/delete are admin-gated), so this needs no permission change.
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [templateValues, setTemplateValues] = useState({});
  const [templatePreview, setTemplatePreview] = useState('');
  const template = useMemo(() => templates.find(t => String(t.id) === String(templateId)), [templates, templateId]);
  const placeholders = useMemo(() => template?.placeholders || [], [template]);

  useEffect(() => {
    templatesAPI.getAll({ isActive: true, limit: 100 })
      .then(res => setTemplates((res.data.data?.templates || res.data.data || []).filter(isAirportTemplate)))
      .catch(() => {});
  }, []);

  // Pre-fill what we already know about this gate's flight ({FlightNumber},
  // {Gate}, {Destination}, ...). A new template starts fresh; a late-arriving
  // flight lookup only fills fields still empty, never overwriting user input.
  const flightCtx = useGateFlightContext(gateId, isOperator ? mySession?.id : undefined);
  const prefillTemplateRef = useRef(null);
  useEffect(() => {
    const known = knownPlaceholderValues(placeholders, flightCtx);
    if (prefillTemplateRef.current !== templateId) {
      prefillTemplateRef.current = templateId;
      setTemplateValues(known);
      setTemplatePreview('');
    } else {
      setTemplateValues(v => fillEmpty(v, known));
    }
  }, [templateId, placeholders, flightCtx]);

  useEffect(() => {
    if (!templateId) return;
    // Debounced — previewing on every keystroke burned through the API rate
    // limit and stacked "Too many requests" toasts. Preview is best-effort, so
    // it never toasts; a stale response is dropped if the user kept typing.
    let stale = false;
    const timer = setTimeout(() => {
      templatesAPI.preview(templateId, templateValues, { skipErrorToast: true })
        .then(res => { if (!stale) setTemplatePreview(res.data.data?.filledText || ''); })
        .catch(() => {});
    }, PREVIEW_DEBOUNCE_MS);
    return () => { stale = true; clearTimeout(timer); };
  }, [templateId, templateValues]);

  useEffect(() => {
    if (lockedGateId) setGateId(lockedGateId);
  }, [lockedGateId]);

  const [timingType, setTimingType] = useState('relative');
  const [relativeMinutes, setRelativeMinutes] = useState(5);
  const [scheduledAt, setScheduledAt] = useState('');
  const [insertAfterId, setInsertAfterId] = useState('');
  const [upcoming, setUpcoming] = useState([]);

  useEffect(() => {
    gatesAPI.getAll()
      .then(res => setGates(res.data.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!gateId || timingType !== 'sequence') return;
    gateQueueAPI.getQueue(gateId)
      .then(res => setUpcoming(res.data.data?.upcoming || []))
      .catch(() => setUpcoming([]));
  }, [gateId, timingType]);

  async function handleSend() {
    if (!text.trim()) {
      toast.error('Please enter announcement text');
      return;
    }
    if (!gateId) {
      toast.error('Please select a gate');
      return;
    }
    if (timingType === 'clock' && !scheduledAt) {
      toast.error('Please choose a playback time');
      return;
    }
    try {
      setSending(true);
      await api.post(`/gates/${gateId}/adhoc`, {
        text: text.trim(),
        timing_type: timingType,
        relative_minutes: timingType === 'relative' ? Number(relativeMinutes) || 0 : undefined,
        scheduled_at: timingType === 'clock' ? new Date(scheduledAt).toISOString() : undefined,
        insert_after_id: timingType === 'sequence' ? (insertAfterId || null) : undefined,
      });
      setLastSent({ text: text.trim(), time: new Date() });
      setText('');
      toast.success('Submitted — a supervisor or manager will review it before it plays');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit announcement');
    } finally {
      setSending(false);
    }
  }

  function insertTemplateText() {
    setText(templatePreview || template?.template_text || '');
  }

  return (
    <div className="px-4 py-5 max-w-lg mx-auto">
      <PageHeader
        icon={Mic}
        title="Ad-Hoc Announcement"
        subtitle="Request an announcement for a gate speaker — a supervisor or manager approves it before it plays"
      />

      {/* Gate selector — an Operator is locked to the gate they're currently working;
          Supervisor/Manager/Admin may choose any gate. */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Gate *</label>
        {isOperator ? (
          mySession ? (
            <div className="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm bg-gray-50 flex items-center justify-between text-gray-700 font-medium">
              <div className="flex items-center gap-2">
                <span>{mySession.gate?.gate_code || `Gate #${mySession.gate_id}`}</span>
                {mySession.flight_number && (
                  <span className="flex items-center gap-1 text-xs text-gray-500 font-normal">
                    <PlaneTakeoff size={11} className="text-gray-400" />
                    {mySession.flight_number}
                  </span>
                )}
              </div>
              <Lock size={13} className="text-gray-400" />
            </div>
          ) : (
            <div className="w-full border border-amber-200 rounded-xl px-3 py-3 text-sm bg-amber-50 flex items-center gap-2 text-amber-700">
              <AlertTriangle size={15} className="flex-shrink-0" />
              You need an active gate session before requesting an ad-hoc announcement.
            </div>
          )
        ) : (
          <select
            value={gateId}
            onChange={e => setGateId(e.target.value)}
            className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select gate</option>
            {gates.map(g => (
              <option key={g.id} value={g.id}>
                {g.gate_code}{g.terminal ? ` · ${g.terminal}` : ''}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Template picker — same admin-managed library as Admin → Templates */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <FileText size={13} className="inline mr-1 text-gray-400" />
          Start from a template (optional)
        </label>
        <select
          value={templateId}
          onChange={e => setTemplateId(e.target.value)}
          className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">None — write from scratch</option>
          {templates.map(t => (
            <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
          ))}
        </select>

        {template && (
          <div className="mt-2 space-y-2">
            {placeholders.length > 0 && (
              <div className="space-y-2">
                {placeholders.map(p => (
                  <div key={p}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{p}</label>
                    <input
                      type="text"
                      value={templateValues[p] || ''}
                      onChange={e => setTemplateValues(v => ({ ...v, [p]: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Preview</p>
              <p className="text-sm text-gray-800">{templatePreview || template.template_text}</p>
            </div>
            <Button variant="primaryLight" fullWidth onClick={insertTemplateText}>
              Insert into announcement text
            </Button>
          </div>
        )}
      </div>

      {/* Text area */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Announcement Text</label>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={6}
          placeholder="Type your announcement here…"
          className="w-full border border-gray-300 rounded-xl px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
        />
        <p className="text-xs text-gray-400 mt-1 text-right">{text.length} characters</p>
      </div>

      {/* Playback timing (CR 8.3) */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-700 mb-1">When should this play?</label>
        <div className="flex gap-2 mb-2">
          {TIMING_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTimingType(opt.value)}
              className={`flex-1 px-2 py-2 rounded-xl text-xs font-medium border transition-colors ${
                timingType === opt.value
                  ? 'bg-blue-700 text-white border-blue-700'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {timingType === 'relative' && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">In</span>
            <input
              type="number"
              min={0}
              value={relativeMinutes}
              onChange={e => setRelativeMinutes(e.target.value)}
              className="w-20 border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">minute(s) — 0 means as soon as possible</span>
          </div>
        )}

        {timingType === 'clock' && (
          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={e => setScheduledAt(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )}

        {timingType === 'sequence' && (
          <select
            value={insertAfterId}
            onChange={e => setInsertAfterId(e.target.value)}
            disabled={!gateId}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
          >
            <option value="">Play first, before anything queued</option>
            {upcoming.map(a => (
              <option key={a.id} value={a.id}>
                After: {(a.english_text || a.announcement_type || '').slice(0, 60)}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Send button */}
      <Button
        variant="primary"
        size="xl"
        fullWidth
        icon={Send}
        loading={sending}
        disabled={!text.trim() || !gateId}
        onClick={handleSend}
        className="shadow"
      >
        {sending ? 'Submitting…' : 'Submit for Approval'}
      </Button>

      {/* Last submitted confirmation */}
      {lastSent && (
        <div className="mt-5 bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle size={16} className="text-yellow-600" />
            <span className="text-sm font-semibold text-yellow-800">Submitted — awaiting approval</span>
            <span className="text-xs text-yellow-600 ml-auto flex items-center gap-1">
              <Clock size={11} />
              {lastSent.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          <p className="text-xs text-yellow-700 line-clamp-2">{lastSent.text}</p>
        </div>
      )}
    </div>
  );
}
